import { CanceledError } from 'axios';
import { DateTime } from 'luxon';
import { action, makeAutoObservable } from 'mobx';
import { PermissionsError } from '../../api/Api';
import { EntityApi } from '../../api/EntityApi';
import {
  PostingApiResponse,
  PostingLineApiResponse,
  CalculationApi,
  PostingSummaryApiResponse,
} from '../../api/CalculationApi';
import {
  InvoiceType,
  PostingLineGrouping,
  RelatedEntityType,
} from '../../config/Constants';
import { CurrencyCode } from '../../config/CurrencyCode';
import { convertCurrency } from '../../utils/Numbers';
import { Contact, Entity, TaxRate } from './Entity';
import { DailyExchangeRates } from './ExchangeRates';
import { PostingApi } from '../../api/PostingApi';

export enum PostingType {
  INVOICE = 'invoice',
  JOURNAL = 'journal',
}

export enum PostingState {
  DRAFT = 'draft',
  AUTHORISED = 'authorised',
  VOIDED = 'voided',
}

export enum PostingPurpose {
  INTEREST = 'interest',
  FX_ADJUSTMENT = 'fx_adjustment',
}

export enum LineAmountType {
  EXCLUSIVE = 'Exclusive',
  INCLUSIVE = 'Inclusive',
  NO_TAX = 'NoTax',
}

export enum JournalLineType {
  CREDIT = 'credit',
  DEBIT = 'debit',
}

export class PostingLine {
  key: string = '';
  name: string = '';
  ruleApportionmentId: number = 0;
  nominalCode: string = '';
  trackingCategories: Map<string, string> = new Map();
  subtotal: number = 0;
  taxAmount: number = 0;
  amount: number = 0;
  taxRate: TaxRate = null;
  currency: CurrencyCode = CurrencyCode.GBP;
  invoiceType: InvoiceType = InvoiceType.PURCHASE;
  journalLineType: JournalLineType = JournalLineType.CREDIT;
  isBalancingLine: boolean = false;

  taxRateError: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: PostingLineApiResponse, invoiceType: InvoiceType) {
    const line = new PostingLine();

    line.invoiceType = invoiceType;
    line.key = apiData.key;
    line.name = apiData.name;
    line.ruleApportionmentId = apiData.ruleApportionmentId;
    line.nominalCode = apiData.nominalCode;
    line.trackingCategories = apiData.trackingCategories
      ? new Map(apiData.trackingCategories.map(cat => [cat.split(':')[0], cat]))
      : new Map();
    line.taxRate =
      apiData.taxRate && apiData.taxRate.id
        ? TaxRate.fromApi(apiData.taxRate)
        : null;
    line.subtotal = apiData.subtotal;
    line.taxAmount = apiData.taxAmount;
    line.amount = apiData.amount;
    line.currency = apiData.currency as CurrencyCode;
    line.isBalancingLine = apiData.balancingLine;
    line.journalLineType =
      apiData.amount > 0 ? JournalLineType.DEBIT : JournalLineType.CREDIT;

    return line;
  }

  toApi = (
    postingType: PostingType,
    currency: CurrencyCode,
    exchangeRates: DailyExchangeRates
  ): PostingLineApiResponse => {
    return {
      key: this.key,
      name: this.name,
      ruleApportionmentId: this.ruleApportionmentId,
      nominalCode: this.nominalCode,
      subtotal: convertCurrency(
        this.subtotal,
        this.currency,
        currency,
        exchangeRates
      ),
      taxAmount: convertCurrency(
        this.taxAmount,
        this.currency,
        currency,
        exchangeRates
      ),
      taxRate: this.taxRate ? { taxType: this.taxRate.taxType } : null,
      amount: convertCurrency(
        this.amount,
        this.currency,
        currency,
        exchangeRates
      ),
      trackingCategories: [...this.trackingCategories.values()],
      currency: currency,
      balancingLine: this.isBalancingLine,
    };
  };

  swapSign = (): void => {
    this.amount *= -1;
    this.subtotal *= -1;
    this.taxAmount *= -1;

    this.journalLineType =
      this.amount > 0 ? JournalLineType.DEBIT : JournalLineType.CREDIT;
  };

  isValid = (entity: Entity): boolean => {
    this.taxRateError =
      this.taxRate === null ||
      !entity
        .taxRateOptions(entity.getNominalCode(this.nominalCode))
        ?.has(this.taxRate.taxType);

    return !this.taxRateError;
  };
}

export class Posting {
  id: number = 0;
  groupId: number = 0;
  created: DateTime = null;
  entity: Entity = null;
  fromEntityId: number = 0;
  toEntityId: number = 0;
  externalId: string = '';
  postedAt: DateTime = null;
  type: PostingType = PostingType.INVOICE;
  state: PostingState = PostingState.DRAFT;
  xeroState: PostingState = PostingState.DRAFT;
  invoiceType: InvoiceType = InvoiceType.PURCHASE;
  currency: CurrencyCode = CurrencyCode.GBP;
  purpose: PostingPurpose;
  issueDate: DateTime = DateTime.now();
  dueDate: DateTime = DateTime.now();
  invoiceNumber: string = '';
  reference: string = '';
  contact: string = '';
  newContactName: string = '';
  lines: PostingLine[] = [];
  lineAmountTypes: LineAmountType = LineAmountType.EXCLUSIVE;
  isOutdated: boolean = false;
  sourceUrl: string = '';
  paidToNominalCode: string = '';
  grouping: PostingLineGrouping = PostingLineGrouping.rule;

  calculationId: number = 0;
  relatedEntityType: RelatedEntityType = null;
  relatedEntityId: number = 0;

  // dirty is whether our version here is dirty compared with what's on Mayday's servers
  // xeroDirty is whether our version here is dirty compared with what's on Xero
  _dirty: boolean = false;
  xeroDirty: boolean = false;

  partialLoad: boolean = true;
  firstLoad: boolean = true;

  exchangeRates: DailyExchangeRates = new DailyExchangeRates();

  balancingLine: PostingLine = null;

  validationErrors: string[] = [];

  updateAbortController: AbortController = null;

  _markAsPaid: boolean = null;

  constructor() {
    makeAutoObservable(this);
    this.updateAbortController = new AbortController();
  }

  // TODO: Bit of a hack, tidy this up
  static fromApi(apiData: PostingSummaryApiResponse | PostingApiResponse) {
    const invoice = new Posting();
    invoice.parseApi(apiData as PostingApiResponse);
    return invoice;
  }

  parseApi = action((apiData: PostingApiResponse): void => {
    if (apiData.id) this.id = apiData.id;
    if (apiData.created)
      this.created = DateTime.fromISO(apiData.created, { zone: 'utc' });
    if (apiData.postedAt)
      this.postedAt = DateTime.fromISO(apiData.postedAt, { zone: 'utc' });
    this.fromEntityId = apiData.from;
    this.toEntityId = apiData.to;
    if (apiData.invoiceType)
      this.invoiceType = apiData.invoiceType as InvoiceType;
    this.isOutdated = apiData.outdated;
    if (apiData.type) this.type = apiData.type as PostingType;
    if (apiData.state) this.state = apiData.state as PostingState;
    if (apiData.xeroState) this.xeroState = apiData.xeroState as PostingState;
    if (apiData.purpose) this.purpose = apiData.purpose as PostingPurpose;
    if (apiData.currency) this.currency = apiData.currency as CurrencyCode;
    if (apiData.issueDate) this.issueDate = DateTime.fromISO(apiData.issueDate);
    if (apiData.dueDate) this.dueDate = DateTime.fromISO(apiData.dueDate);
    if (apiData.invoiceNumber) this.invoiceNumber = apiData.invoiceNumber;
    if (apiData.reference) this.reference = apiData.reference;
    if (apiData.externalId) this.externalId = apiData.externalId;
    if (apiData.contactId) this.contact = apiData.contactId;
    if (apiData.lineAmountType)
      this.lineAmountTypes = apiData.lineAmountType as LineAmountType;
    if (apiData.grouping)
      this.grouping = apiData.grouping as PostingLineGrouping;

    // The backend should never change the value on paidToNominalCode unless we ask it to,
    // so let's not override any changes the user makes when we reload from the API
    if (this.firstLoad) {
      if (apiData.paidToNominalCode)
        this.paidToNominalCode = apiData.paidToNominalCode;
      this._markAsPaid = apiData.paidToNominalCode ? true : false;
    }

    this._dirty = false;

    this.relatedEntityId = apiData.relatedEntityId;
    this.relatedEntityType = apiData.relatedEntityType as RelatedEntityType;

    if (this.relatedEntityType === RelatedEntityType.CALCULATION) {
      this.calculationId = apiData.relatedEntityId;
    }

    // If the API response was partial, mark it as so
    this.lines = [];
    if (apiData.lines) {
      apiData.lines.forEach(line => {
        const invoiceLine = PostingLine.fromApi(line, this.invoiceType);

        if (invoiceLine.isBalancingLine) {
          this.balancingLine = invoiceLine;
        } else {
          this.lines.push(invoiceLine);
        }
      });
      this.partialLoad = false;
    } else {
      this.partialLoad = true;
    }

    this.exchangeRates = DailyExchangeRates.fromApi(apiData.exchangeRates);
    this.firstLoad = false;
  });

  toApi = (): PostingApiResponse => {
    const lines = [...this.lines];

    if (this.type === PostingType.JOURNAL) {
      lines.push(this.balancingLine);
    }

    return {
      id: this.id > 0 ? this.id : null,
      created: this.created ? this.created.toISO() : null,
      postedAt: this.postedAt ? this.postedAt.toISO() : null,
      from: this.fromEntityId,
      to: this.toEntityId,
      relatedEntityType: this.relatedEntityType,
      relatedEntityId: this.relatedEntityId,
      invoiceType: this.invoiceType,
      type: this.type,
      state: this.state,
      xeroState: this.xeroState,
      purpose: this.purpose,
      issueDate: this.issueDate.toISODate(),
      dueDate: this.dueDate.toISODate(),
      invoiceNumber: this.invoiceNumber,
      reference: this.reference,
      currency: this.currency,
      externalId: this.externalId,
      contactId: this.contact !== '-1' ? this.contact : null,
      lines: lines
        .filter(line => line !== null)
        .map(line => line.toApi(this.type, this.currency, this.exchangeRates)),
      lineAmountType: this.lineAmountTypes,
      sourceUrl: this.sourceUrl,
      paidToNominalCode: this.markAsPaid ? this.paidToNominalCode : null,
      grouping: this.grouping,
    };
  };

  createContactIfNeeded = async (entity: Entity) => {
    if (this.type === PostingType.INVOICE && this.contact === '-1') {
      const contactResponse = await EntityApi.createContact(
        this.groupId,
        entity.id,
        this.newContactName
      );
      this.contact = contactResponse.externalId;
      // Backend returns: 200 when you attempt to create a contact that already exists, 201 when a new contact has been created
      if (contactResponse.httpStatus === 201) {
        entity.contacts.push(Contact.fromApi(contactResponse));
      }
    }
  };

  reload = async (): Promise<Posting> => {
    return CalculationApi.getPosting(
      this.groupId,
      this.calculationId,
      this.id
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      return this;
    });
  };

  update = async (): Promise<Posting> => {
    if (this.relatedEntityType === RelatedEntityType.CALCULATION) {
      return this.updateForCalculation();
    }

    return PostingApi.update(
      this.groupId,
      this.fromEntityId,
      this.id,
      this.toApi()
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      return this;
    });
  };

  updateForCalculation = async (): Promise<Posting> => {
    this.updateAbortController.abort();
    this.updateAbortController = new AbortController();

    return CalculationApi.updatePosting(
      this.groupId,
      this.calculationId,
      this.id,
      this.toApi(),
      this.updateAbortController
    )
      .then((response: PostingApiResponse) => {
        this.parseApi(response);
        return this;
      })
      .catch(e => {
        if (e instanceof CanceledError) {
          return this;
        } else if (e instanceof PermissionsError) {
          // Likely this is only hit by the support account
          this._dirty = false;
          return this;
        }
        throw e;
      });
  };

  create = async (entity: Entity): Promise<Posting> => {
    return PostingApi.create(this.groupId, entity.id, this.toApi()).then(
      (response: PostingApiResponse) => {
        this.parseApi(response);
        return this;
      }
    );
  };

  markLoanAsPaid = async (entity: Entity): Promise<Posting> => {
    return PostingApi.update(
      this.groupId,
      entity.id,
      this.id,
      this.toApi(),
      false,
      false,
      true
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      return this;
    });
  };

  submit = async (entity: Entity): Promise<Posting> => {
    await this.createContactIfNeeded(entity);

    if (this.relatedEntityType === RelatedEntityType.CALCULATION) {
      return this.submitForCalculation();
    }

    return PostingApi.update(
      this.groupId,
      entity.id,
      this.id,
      this.toApi(),
      true
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      return this;
    });
  };

  private submitForCalculation = async (): Promise<Posting> => {
    this.updateAbortController.abort();
    this.updateAbortController = new AbortController();

    return CalculationApi.updatePostingAndSubmit(
      this.groupId,
      this.calculationId,
      this.id,
      this.toApi(),
      this.updateAbortController
    )
      .then((response: PostingApiResponse) => {
        this.parseApi(response);
        this.xeroDirty = false;
        return this;
      })
      .catch(e => {
        if (e instanceof CanceledError) {
          return this;
        }
        throw e;
      });
  };

  markPaid = async (): Promise<Posting> => {
    this.updateAbortController.abort();
    this.updateAbortController = new AbortController();

    return CalculationApi.markAsPaid(
      this.groupId,
      this.calculationId,
      this.id,
      this.toApi(),
      this.updateAbortController
    )
      .then((response: PostingApiResponse) => {
        this.parseApi(response);
        this.xeroDirty = false;
        return this;
      })
      .catch(e => {
        if (e instanceof CanceledError) {
          return this;
        }
        throw e;
      });
  };

  recreate = async (entity: Entity): Promise<Posting> => {
    if (this.relatedEntityType === RelatedEntityType.CALCULATION) {
      return this.recreateForCalculation(entity);
    }

    return PostingApi.update(
      this.groupId,
      entity.id,
      this.id,
      this.toApi(),
      true,
      true
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      return this;
    });
  };

  recreateForCalculation = async (entity: Entity): Promise<Posting> => {
    await this.createContactIfNeeded(entity);

    this.updateAbortController.abort();
    this.updateAbortController = new AbortController();

    return CalculationApi.recreatePosting(
      this.groupId,
      this.calculationId,
      this.id,
      this.toApi(),
      this.updateAbortController
    ).then((response: PostingApiResponse) => {
      this.parseApi(response);
      this.xeroDirty = false;
      return this;
    });
  };

  isValid = (entity: Entity): boolean => {
    this.validationErrors = [];

    if (this.issueDate === null) {
      this.validationErrors.push('issueDate');
    }

    if (this.type === PostingType.INVOICE) {
      if (this.contact === '-1' && this.newContactName === '') {
        this.validationErrors.push('contact');
      }

      if (this.invoiceNumber === '') {
        this.validationErrors.push('invoiceNumber');
      }

      if (this.dueDate === null) {
        this.validationErrors.push('dueDate');
      }

      if (this.markAsPaid && !this.paidToNominalCode) {
        this.validationErrors.push('paidToNominalCode');
      }
    }

    if (this.type === PostingType.JOURNAL) {
      if (this.reference === '') {
        this.validationErrors.push('reference');
      }

      // Intra-entity journals do not have balancing lines
      if (
        this.fromEntityId !== this.toEntityId &&
        this.balancingLine.nominalCode === ''
      ) {
        this.validationErrors.push('balancingLineNominalCode');
      }
    }

    const invalidLines = this.lines
      .map(line => line.isValid(entity))
      .filter(valid => !valid);

    return this.validationErrors.length === 0 && invalidLines.length === 0;
  };

  deeplinkUrl = (entity: Entity): string => {
    if (this.type === PostingType.JOURNAL) {
      return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${entity.shortcode}&redirecturl=/Journal/View.aspx%3FinvoiceID=${this.externalId}`;
    }

    const redirect =
      this.invoiceType === InvoiceType.SALES
        ? `/AccountsReceivable/View.aspx%3FInvoiceID=${this.externalId}`
        : `/AccountsPayable/View.aspx%3FInvoiceID=${this.externalId}`;
    return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${entity.shortcode}&redirecturl=${redirect}`;
  };

  niceState = (): string => {
    if (this.state === PostingState.DRAFT) return 'draft';
    return this.type === PostingType.JOURNAL ? 'posted' : 'authorised';
  };

  niceType = (): string => {
    if (this.type === PostingType.JOURNAL) {
      return 'Postings.Types.journal';
    } else {
      return `Postings.Types.${
        this.invoiceType === InvoiceType.PURCHASE ? 'bill' : 'invoice'
      }`;
    }
  };

  hasBeenPosted = (): boolean => {
    return this.externalId && this.externalId !== '';
  };

  get dirty(): boolean {
    return this._dirty;
  }

  set dirty(isDirty: boolean) {
    this._dirty = isDirty;

    if (isDirty) {
      this.update();
      this.setXeroDirty();
    }
  }

  setXeroDirty = () => {
    if (this.hasBeenPosted()) {
      this.xeroDirty = true;
    }
  };

  get markAsPaid(): boolean {
    return this.state === PostingState.AUTHORISED && this._markAsPaid;
  }

  set markAsPaid(v: boolean) {
    this._markAsPaid = v;
  }

  taxTotal = () => {
    return this.lines
      .filter(
        line => !(line.isBalancingLine && this.type === PostingType.JOURNAL)
      )
      .map(line =>
        convertCurrency(
          line.taxAmount,
          line.currency as CurrencyCode,
          this.currency,
          this.exchangeRates
        )
      )
      .reduce((a, b) => a + b, 0);
  };

  subTotal = () => {
    return this.lines
      .filter(
        line => !(line.isBalancingLine && this.type === PostingType.JOURNAL)
      )
      .map(line =>
        convertCurrency(
          line.subtotal,
          line.currency as CurrencyCode,
          this.currency,
          this.exchangeRates
        )
      )
      .reduce((a, b) => a + b, 0);
  };
}
